import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';

const formatDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StatusBadge = ({ active, suspended }) => {
  if (suspended) return <span data-testid="user-status-badge" className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Suspended</span>;
  return (
    <span data-testid="user-status-badge" className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

/* ─── Confirm Modal ─── */
const ConfirmModal = ({ title, message, confirmLabel, confirmColor, children, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={onCancel}>
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="confirm-modal">
      <div className={`px-6 py-4 rounded-t-xl ${confirmColor === 'red' ? 'bg-red-600' : 'bg-amber-500'}`}>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-700">{message}</p>
        {children}
      </div>
      <div className="px-6 py-4 border-t flex justify-end gap-3">
        <button data-testid="confirm-cancel-btn" onClick={onCancel} disabled={loading} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button
          data-testid="confirm-action-btn"
          onClick={onConfirm}
          disabled={loading}
          className={`px-5 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${confirmColor === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}`}
        >
          {loading ? 'Processing...' : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

/* ─── User Edit Modal ─── */
const UserEditModal = ({ user, onClose, onSave }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user) setForm({ ...user }); }, [user]);
  if (!user) return null;

  const isAdmin = user.user_type === 'admin';

  const readOnlyFields = [
    'username', 'email', 'push_token', 'push_token_updated_at',
    'last_login_timestamp', 'last_logout_timestamp', 'created_at',
    ...(isAdmin ? [] : ['active_device_id', 'active_device_name', 'active_device_platform', 'last_active_at', 'last_login_location']),
  ];

  const adminFields = [
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'address', label: 'Address' },
    { key: 'date_of_birth', label: 'Date of Birth' },
    { key: 'active', label: 'Active', type: 'toggle' },
    { key: 'push_token', label: 'Push Token' },
    { key: 'push_token_updated_at', label: 'Push Token Updated' },
    { key: 'last_login_timestamp', label: 'Last Login' },
    { key: 'last_logout_timestamp', label: 'Last Logout' },
    { key: 'created_at', label: 'Created At' },
  ];

  const teamFields = [
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'address', label: 'Address' },
    { key: 'date_of_birth', label: 'Date of Birth' },
    { key: 'middle_name', label: 'Middle Name' },
    { key: 'active', label: 'Active', type: 'toggle' },
    { key: 'active_device_id', label: 'Device ID' },
    { key: 'active_device_name', label: 'Device Name' },
    { key: 'active_device_platform', label: 'Device Platform' },
    { key: 'push_token', label: 'Push Token' },
    { key: 'push_token_updated_at', label: 'Push Token Updated' },
    { key: 'last_login_timestamp', label: 'Last Login' },
    { key: 'last_logout_timestamp', label: 'Last Logout' },
    { key: 'last_active_at', label: 'Last Active' },
    { key: 'last_login_location', label: 'Last Login Location' },
    { key: 'created_at', label: 'Created At' },
  ];

  const fields = isAdmin ? adminFields : teamFields;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(user.id, { ...form, user_type: user.user_type });
      onClose();
    } catch {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => {
    const isReadOnly = readOnlyFields.includes(f.key);
    const value = form[f.key];

    if (f.type === 'toggle') {
      return (
        <button
          data-testid={`modal-field-${f.key}`}
          disabled={isReadOnly}
          onClick={() => !isReadOnly && setForm(p => ({ ...p, [f.key]: !p[f.key] }))}
          className={`relative w-11 h-6 rounded-full transition-colors ${value ? 'bg-green-500' : 'bg-gray-300'} ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : ''}`} />
        </button>
      );
    }

    const isTimestamp = f.key.includes('timestamp') || f.key.includes('_at') || f.key === 'created_at';
    const displayValue = isTimestamp ? formatDateTime(value) : (value ?? '');

    if (isReadOnly) {
      return (
        <div data-testid={`modal-field-${f.key}`} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 select-all">
          {typeof displayValue === 'object' ? JSON.stringify(displayValue) : String(displayValue || '—')}
        </div>
      );
    }

    return (
      <input
        data-testid={`modal-field-${f.key}`}
        value={value ?? ''}
        onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
      />
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="user-edit-modal">
        <div className="px-6 py-4 bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-t-xl">
          <h3 className="text-lg font-semibold text-white">{form.first_name} {form.last_name}</h3>
          <p className="text-sm text-purple-200">{isAdmin ? 'Organisation Admin' : `Team Member — ${user.role || 'N/A'}`}</p>
        </div>
        <div className="p-6 space-y-4">
          {fields.map(f => (
            <div key={f.key} className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-2">
                {f.label}
                {readOnlyFields.includes(f.key) && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded">Read Only</span>}
              </label>
              {renderField(f)}
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button data-testid="modal-cancel-btn" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">Cancel</button>
          <button data-testid="modal-save-btn" onClick={handleSave} disabled={saving} className="px-5 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
export default function OrganisationDataPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [editUser, setEditUser] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortKey, setSortKey] = useState('first_name');
  const [sortDir, setSortDir] = useState('asc');

  // Action modals
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/super-admin/organisation-data`);
      setData(res.data.organisations || []);
    } catch (err) {
      console.error('Failed to fetch organisation data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleExpand = (orgId) => setExpanded(p => ({ ...p, [orgId]: !p[orgId] }));

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortHeader = ({ label, field }) => (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:text-purple-300 select-none" onClick={() => handleSort(field)}>
      <span className="flex items-center gap-1">
        {label}
        {sortKey === field && (
          <svg className={`w-3 h-3 transition-transform ${sortDir === 'desc' ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
          </svg>
        )}
      </span>
    </th>
  );

  const filteredOrgs = useMemo(() => {
    const lc = search.toLowerCase();
    return data.map(org => {
      let users = org.users || [];
      if (filterStatus !== 'all') {
        if (filterStatus === 'active') users = users.filter(u => u.active && !u.suspended);
        else if (filterStatus === 'inactive') users = users.filter(u => !u.active);
        else if (filterStatus === 'suspended') users = users.filter(u => u.suspended);
      }
      if (filterType !== 'all') users = users.filter(u => u.user_type === filterType);
      if (lc) {
        users = users.filter(u =>
          (u.username || '').toLowerCase().includes(lc) ||
          (u.email || '').toLowerCase().includes(lc) ||
          (u.first_name || '').toLowerCase().includes(lc) ||
          (u.last_name || '').toLowerCase().includes(lc) ||
          (u.phone_number || '').toLowerCase().includes(lc)
        );
      }
      // Sort: admins always first, then by selected column
      users = [...users].sort((a, b) => {
        if (a.user_type === 'admin' && b.user_type !== 'admin') return -1;
        if (a.user_type !== 'admin' && b.user_type === 'admin') return 1;
        const av = (a[sortKey] ?? '').toString().toLowerCase();
        const bv = (b[sortKey] ?? '').toString().toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
      return { ...org, users };
    }).filter(org => {
      if (!lc && filterStatus === 'all' && filterType === 'all') return true;
      if (org.users.length > 0) return true;
      if (lc && (org.name.toLowerCase().includes(lc) || org.organisation_id.toLowerCase().includes(lc))) return true;
      return false;
    });
  }, [data, search, filterStatus, filterType, sortKey, sortDir]);

  const handleSaveUser = async (userId, payload) => {
    await axios.put(`${API}/super-admin/organisation-data/users/${userId}`, payload);
    await fetchData();
  };

  const handleSuspend = async () => {
    if (!suspendTarget) return;
    setActionLoading(true);
    try {
      const isSuspending = !suspendTarget.suspended;
      await axios.post(`${API}/super-admin/organisation-data/users/${suspendTarget.id}/suspend`, {
        suspend: isSuspending,
        reason: isSuspending ? suspendReason : '',
      });
      await fetchData();
      setSuspendTarget(null);
      setSuspendReason('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update suspension status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleteConfirmText !== deleteTarget.username) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/super-admin/organisation-data/users/${deleteTarget.id}`, {
        data: { user_type: deleteTarget.user_type },
      });
      await fetchData();
      setDeleteTarget(null);
      setDeleteConfirmText('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const totalUsers = data.reduce((s, o) => s + (o.users?.length || 0), 0);
  const totalAdmins = data.reduce((s, o) => s + (o.users?.filter(u => u.user_type === 'admin').length || 0), 0);
  const totalTeam = totalUsers - totalAdmins;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="organisation-data-page" className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold">Organisation Data</h1>
            <p className="text-sm text-purple-200">{data.length} organisations &middot; {totalAdmins} admins &middot; {totalTeam} team members</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="search-input"
          placeholder="Search by name, email, username, phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[220px] px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
        />
        <select data-testid="filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>
        <select data-testid="filter-type" value={filterType} onChange={e => setFilterType(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="all">All Types</option>
          <option value="admin">Admins</option>
          <option value="team">Team Members</option>
        </select>
      </div>

      {/* Org cards */}
      <div className="space-y-4">
        {filteredOrgs.map(org => {
          const isExpanded = expanded[org.organisation_id];
          return (
            <div key={org.organisation_id} data-testid={`org-card-${org.organisation_id}`} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <button
                data-testid={`org-toggle-${org.organisation_id}`}
                onClick={() => toggleExpand(org.organisation_id)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <svg className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{org.name}</p>
                    <p className="text-xs text-gray-500">{org.organisation_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm text-gray-500 flex-shrink-0">
                  <span>{org.city || '—'}</span>
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">{org.subscription_tier || '—'}</span>
                  <span>{formatDate(org.registration_date)}</span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{org.users.length} user{org.users.length !== 1 ? 's' : ''}</span>
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-gray-100">
                  {org.users.length === 0 ? (
                    <p className="px-5 py-6 text-center text-sm text-gray-400">No users match the current filters</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 text-white">
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider w-16">Type</th>
                            <SortHeader label="Username" field="username" />
                            <SortHeader label="Email" field="email" />
                            <SortHeader label="First Name" field="first_name" />
                            <SortHeader label="Last Name" field="last_name" />
                            <SortHeader label="Phone" field="phone_number" />
                            <SortHeader label="Address" field="address" />
                            <SortHeader label="DOB" field="date_of_birth" />
                            <SortHeader label="Status" field="active" />
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {org.users.map(u => (
                            <tr
                              key={u.id}
                              data-testid={`user-row-${u.id}`}
                              className={`hover:bg-purple-50/50 cursor-pointer transition-colors ${u.suspended ? 'bg-red-50/40' : ''}`}
                              onClick={() => setEditUser(u)}
                            >
                              <td className="px-4 py-3">
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${u.user_type === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {u.user_type === 'admin' ? 'Admin' : 'Team'}
                                </span>
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-900">{u.username || '—'}</td>
                              <td className="px-4 py-3 text-gray-600">{u.email || '—'}</td>
                              <td className="px-4 py-3">{u.first_name || '—'}</td>
                              <td className="px-4 py-3">{u.last_name || '—'}</td>
                              <td className="px-4 py-3 text-gray-600">{u.phone_number || '—'}</td>
                              <td className="px-4 py-3 text-gray-600 max-w-[150px] truncate">{u.address || '—'}</td>
                              <td className="px-4 py-3 text-gray-600">{formatDate(u.date_of_birth)}</td>
                              <td className="px-4 py-3"><StatusBadge active={u.active} suspended={u.suspended} /></td>
                              <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                {u.user_type === 'team' ? (
                                  <div className="flex items-center gap-1">
                                    <button
                                      data-testid={`action-suspend-${u.id}`}
                                      onClick={() => { setSuspendTarget(u); setSuspendReason(''); }}
                                      className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${u.suspended ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
                                    >
                                      {u.suspended ? 'Unsuspend' : 'Suspend'}
                                    </button>
                                    <button
                                      data-testid={`action-delete-${u.id}`}
                                      onClick={() => { setDeleteTarget(u); setDeleteConfirmText(''); }}
                                      className="px-2.5 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filteredOrgs.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <p className="text-lg font-medium">No organisations found</p>
            <p className="text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editUser && <UserEditModal user={editUser} onClose={() => setEditUser(null)} onSave={handleSaveUser} />}

      {/* Suspend / Unsuspend confirmation */}
      {suspendTarget && (
        <ConfirmModal
          title={suspendTarget.suspended ? 'Unsuspend User' : 'Suspend User'}
          message={
            suspendTarget.suspended
              ? `Are you sure you want to unsuspend ${suspendTarget.first_name} ${suspendTarget.last_name} (${suspendTarget.username})? They will be able to log in again.`
              : `Are you sure you want to suspend ${suspendTarget.first_name} ${suspendTarget.last_name} (${suspendTarget.username})? This will immediately block their login and invalidate all active sessions. This cannot be overridden by the organisation admin.`
          }
          confirmLabel={suspendTarget.suspended ? 'Unsuspend' : 'Suspend User'}
          confirmColor={suspendTarget.suspended ? 'amber' : 'red'}
          onConfirm={handleSuspend}
          onCancel={() => { setSuspendTarget(null); setSuspendReason(''); }}
          loading={actionLoading}
        >
          {!suspendTarget.suspended && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Reason for suspension (optional)</label>
              <textarea
                data-testid="suspend-reason-input"
                value={suspendReason}
                onChange={e => setSuspendReason(e.target.value)}
                placeholder="e.g. Inappropriate behaviour, policy violation..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none"
                rows={3}
              />
            </div>
          )}
          {suspendTarget.suspended && suspendTarget.suspended_reason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs font-medium text-red-700 mb-1">Original suspension reason:</p>
              <p className="text-sm text-red-600">{suspendTarget.suspended_reason}</p>
              {suspendTarget.suspended_by && <p className="text-xs text-red-400 mt-1">Suspended by: {suspendTarget.suspended_by}</p>}
            </div>
          )}
        </ConfirmModal>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <ConfirmModal
          title="Delete User Permanently"
          message={`You are about to permanently delete ${deleteTarget.first_name} ${deleteTarget.last_name} (${deleteTarget.username}) from ${deleteTarget.email}. This action cannot be undone. All user data will be removed from the system.`}
          confirmLabel="Delete Permanently"
          confirmColor="red"
          onConfirm={handleDelete}
          onCancel={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
          loading={actionLoading}
        >
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Type <strong>{deleteTarget.username}</strong> to confirm deletion
            </label>
            <input
              data-testid="delete-confirm-input"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder={deleteTarget.username}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
              autoComplete="off"
            />
            {deleteConfirmText && deleteConfirmText !== deleteTarget.username && (
              <p className="text-xs text-red-500 mt-1">Username does not match</p>
            )}
          </div>
        </ConfirmModal>
      )}
    </div>
  );
}
